import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { webCheckWarnings } from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { loadWebCheck, saveWebCheck } from '../licences/api.ts';
import type { AdminTenant } from './api.ts';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/**
 * Braynewave's Tenants page: the seat check at Teslyn Cloud sign-in for this environment (platform setting
 * `licence_web_check`, docs/specs/cloud/licences-page.md §4). Off until go-live; switched on at the go-live step that
 * switches licences on. Before it goes on, the organisations whose staff would be refused are named. Platform admins
 * at two-step sign-in only; every change is audited `platform.setting_changed`.
 */
export function WebCheckPanel({
  sb,
  tenants,
}: {
  readonly sb: SupabaseClient;
  readonly tenants: readonly AdminTenant[];
}): ReactElement {
  const [on, setOn] = useState<boolean | null>(null);
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState('');
  const [notice, setNotice] = useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const reload = useCallback(() => {
    void loadWebCheck(sb).then(setOn);
  }, [sb]);
  useEffect(reload, [reload]);
  const warnings = webCheckWarnings(tenants);

  const save = async (next: boolean): Promise<void> => {
    setAsking(false);
    try {
      await saveWebCheck(sb, next, note);
      setNotice({
        ok: true,
        text: next
          ? 'The seat check at sign-in is on: staff take a seat when they sign in to Teslyn Cloud.'
          : 'The seat check at sign-in is off: staff sign in to Teslyn Cloud without a seat.',
      });
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    }
    reload();
  };

  return (
    <div className="panel stack" aria-labelledby="web-check-title">
      <h2 id="web-check-title" className="heading">
        Seat check at sign-in{' '}
        {on === null ? null : on ? (
          <span className="chip chip--pass label">On</span>
        ) : (
          <span className="chip chip--warn label">Off</span>
        )}
      </h2>
      <p className="body">
        {on
          ? 'Each staff member takes a seat of their organisation the first time they sign in to Teslyn Cloud; with none free they are refused and shown who holds the seats. Clients are never checked.'
          : 'Off until Teslyn goes live: staff sign in to Teslyn Cloud without taking a seat. Teslyn Field’s own check is switched on separately, by the public key in its build.'}
      </p>
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {!asking && on !== null && (
        <div className="row">
          <Button
            variant={on ? 'quiet' : 'primary'}
            onClick={() => {
              setNote('');
              setAsking(true);
            }}
          >
            {on ? 'Switch off' : 'Switch on'}
          </Button>
        </div>
      )}
      {asking && (
        <div className="panel confirm stack" role="dialog" aria-labelledby="web-check-confirm">
          <p id="web-check-confirm" className="body">
            {on
              ? 'Switch the seat check off? Staff sign in without a seat again; the seats already taken stay.'
              : 'Switch the seat check on for every organisation in this environment?'}
          </p>
          {!on && warnings.length > 0 && (
            <ul className="notice notice--warn" aria-label="Would be refused">
              {warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
          <label className="label muted" htmlFor="web-check-note">
            Note for the audit log
          </label>
          <input
            spellCheck
            id="web-check-note"
            className="input"
            value={note}
            maxLength={300}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="row">
            <Button variant="primary" onClick={() => void save(!on)}>
              {on ? 'Switch off' : 'Switch on'}
            </Button>
            <Button variant="quiet" onClick={() => setAsking(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
